/**
 * Analytics events for Google Tag Manager.
 *
 * Ported from the Trusted Choice theme (assets/js/base/_analytics.js). Each event is pushed to the
 * dataLayer as `GAEvent`, and the site's GTM container sends it to GA4 as `{eventCategory}_{eventAction}`:
 * share_modal, share_twitter, share_facebook, share_download, download_click, external_link_click,
 * chat_click, form_input and form_submit.
 *
 * Enqueued only on sites with a G-Tag Id (GTM container) - see src/functions/assets.php.
 */

/**
 * Track an event with whichever tracker is on the page.
 *
 * @param {string} category
 * @param {string} action
 * @param {string} label
 * @return {Promise} Always resolves - after the tracker callback, or after 1s.
 */
const trackEvent = function ( category, action, label ) {
	const track = ( callback ) => {
		if ( window.dataLayer ) { // gtm.js
			window.dataLayer.push( {
				event: 'GAEvent',
				eventCategory: category,
				eventAction: action,
				eventLabel: label,
				eventCallback: () => callback( 'gtm tags fired!' ), // GTM calls this once the event's tags have run
				eventTimeout: 1000,
			} );
		} else if ( typeof window.gtag === 'function' ) { // gtag.js
			window.gtag( 'event', action, {
				event_category: category,
				event_action: action, // Event name is default, this is redundant
				event_label: label,
				event_callback: () => callback( 'gtag event sent!' ),
			} );
		} else if ( typeof window.ga === 'function' ) { // analytics.js
			window.ga( 'send', 'event', category, action, label, {
				hitCallback: () => callback( 'ga event sent!' ),
			} );
		} else {
			callback( 'No analytics tracker exists!' );
		}
	};

	// This promise is ALWAYS resolved to avoid unhandled promise rejection
	return new Promise( ( resolve ) => {
		track( resolve );

		// Resolve if it takes more than 1s
		setTimeout( () => resolve( 'Analytics event timed out after 1s' ), 1000 );
	} );
};

/**
 * Is this click one that leaves the page in the same tab (so the event has to be sent before navigating)?
 *
 * @param {MouseEvent}  event
 * @param {HTMLElement} link
 * @return {boolean}
 */
const leavesPage = ( event, link ) => ! event.defaultPrevented
	&& event.button === 0
	&& ! ( event.ctrlKey || event.metaKey || event.shiftKey || event.altKey )
	&& /^(|_self|_parent|_top)$/i.test( link.target )
	&& ! link.hasAttribute( 'download' );

/**
 * Title of the social resource / content calendar modal that holds a share link.
 *
 * Uses textContent rather than innerText: Big I capitalizes headings with CSS, and TC sent the raw title.
 *
 * @param {HTMLElement} link
 * @return {string}
 */
const getModalTitle = ( link ) => link.closest( '.crl-preview-modal__body, .content-calendar-modal__body' )
	?.querySelector( '.crl-preview-modal__title, .content-calendar-modal__title' )
	?.textContent.trim() ?? '';

const analyticsInit = function () {
	// chat portal clicked
	document.addEventListener( 'click', ( event ) => {
		const button = event.target.closest?.( '.chat-button' );
		if ( button ) {
			trackEvent( 'chat', 'click', button.href );
		}
	} );

	// a link to another site clicked (share icons in the modals are tracked as shares below)
	document.addEventListener( 'click', ( event ) => {
		const link = event.target.closest?.( 'a[href]:not(.crl-preview-modal__icon-link):not(.content-calendar-modal__icon-link)' );
		if ( ! link || ! /^https?:$/.test( link.protocol ) || link.hostname === location.hostname ) {
			return;
		}

		if ( leavesPage( event, link ) ) {
			// Hold the click until the event is sent, otherwise the page unloads first
			event.preventDefault();
			trackEvent( 'external_link', 'click', link.href ).then( () => location.assign( link.href ) );
		} else {
			trackEvent( 'external_link', 'click', link.href );
		}
	} );

	// download link clicked
	document.addEventListener( 'click', ( event ) => {
		const link = event.target.closest?.( 'a[download]' );
		if ( link ) {
			trackEvent( 'download', 'click', link.textContent.trim() || document.title );
		}
	} );

	// Form input
	const inputForms = new WeakSet();
	document.addEventListener( 'input', ( event ) => {
		const form = event.target.closest?.( 'form input, form select, form textarea' )?.closest( 'form' );
		if ( form && ! inputForms.has( form ) ) {
			trackEvent( 'form', 'input', form.action );
			inputForms.add( form ); // Mark as input
		}
	} );

	// Form submit - Gravity Forms submits through jQuery, which doesn't dispatch a native submit event,
	// so use jQuery's delegated handler when it is on the page (it also receives native submits)
	const onSubmit = ( event ) => trackEvent( 'form', 'submit', event.target.action );
	if ( window.jQuery ) {
		window.jQuery( document ).on( 'submit', 'form', onSubmit );
	} else {
		document.addEventListener( 'submit', onSubmit );
	}

	// Share - opening a resource in the social resource list (cards can load later, so delegate)
	document.addEventListener( 'click', ( event ) => {
		const card = event.target.closest?.( '.campaign-resource-list--social [data-crl-preview-trigger]' );
		if ( card ) {
			trackEvent( 'share', 'modal', card.getAttribute( 'data-crl-preview-title' ) );
		}
	} );

	// Share - links in the social resource and content calendar modals
	document.querySelectorAll( '.crl-preview-modal__share-link--x, .content-calendar-modal__share-link--x' ).forEach( ( link ) => {
		link.addEventListener( 'click', () => trackEvent( 'share', 'twitter', getModalTitle( link ) ) );
	} );
	document.querySelectorAll( '.crl-preview-modal__share-link--fb, .content-calendar-modal__share-link--facebook' ).forEach( ( link ) => {
		link.addEventListener( 'click', () => trackEvent( 'share', 'facebook', getModalTitle( link ) ) );
	} );
	document.querySelectorAll( '.crl-preview-modal__download, .content-calendar-modal__download' ).forEach( ( link ) => {
		link.addEventListener( 'click', () => trackEvent( 'share', 'download', getModalTitle( link ) ) );
	} );
};

document.addEventListener( 'DOMContentLoaded', analyticsInit );
